import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, paymentClass, formatDate } from "@/lib/utils-app";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useStores } from "@/lib/stores";
import WhatsAppButton from "@/components/WhatsAppButton";

export default function AdminPayments() {
  const { activeStore } = useStores();
  const [rows, setRows] = useState([]);
  const [manual, setManual] = useState(null);
  const [method, setMethod] = useState("Dinheiro");
  const [amount, setAmount] = useState("");

  const load = async () => {
    try {
      const qs = new URLSearchParams({ status: "payment_pending" });
      if (activeStore) qs.append("store_id", activeStore);
      const { data } = await api.get(`/admin/service-orders?${qs.toString()}`);
      setRows(data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error(e.response?.data?.detail || "Falha ao carregar");
    }
  };
  useEffect(() => { load(); }, [activeStore]);

  const doManual = async () => {
    try {
      await api.post(`/admin/service-orders/${manual.os_code}/manual-payment`, { method, amount: parseFloat(amount || manual.total) });
      toast.success("Baixa registrada.");
      setManual(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl">
      <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Financeiro</div>
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Pagamentos Pendentes</h1>
      <p className="mt-2 text-neutral-600 text-sm">OS concluídas aguardando quitação. Pagamento online é atualizado automaticamente via webhook do Stripe.</p>

      <div className="mt-8 bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">OS</th>
                <th className="px-4 py-3 text-left font-semibold">Cliente</th>
                <th className="px-4 py-3 text-left font-semibold">Status pagamento</th>
                <th className="px-4 py-3 text-right font-semibold">Valor</th>
                <th className="px-4 py-3 text-left font-semibold">Concluído em</th>
                <th className="px-4 py-3 text-right font-semibold">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-neutral-500">Sem cobranças pendentes.</td></tr>}
              {rows.map((o) => (
                <tr key={o.os_code} className="hover:bg-neutral-50" data-testid={`payment-row-${o.os_code}`}>
                  <td className="px-4 py-3 font-mono font-bold">{o.os_code}</td>
                  <td className="px-4 py-3">{o.customer_name}<div className="text-xs text-neutral-500 font-mono">{o.customer_doc}</div></td>
                  <td className="px-4 py-3"><span className={paymentClass(o.payment_status)}>● {o.payment_status}</span></td>
                  <td className="px-4 py-3 text-right font-mono">{money(o.total)}</td>
                  <td className="px-4 py-3 text-neutral-500 text-xs">{formatDate(o.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <WhatsAppButton
                        phone={o.customer_phone}
                        message={`Olá ${o.customer_name}, sua OS ${o.os_code} está pronta! Você pode passar para retirar quando preferir.`}
                        testid={`payment-wa-${o.os_code}`}
                        size="xs"
                      />
                      <Button size="sm" variant="outline" onClick={() => { setManual(o); setAmount(String(o.total)); }} data-testid={`manual-pay-${o.os_code}`}>
                        Baixa manual
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!manual} onOpenChange={(v) => !v && setManual(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Baixa manual — {manual?.os_code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Forma de pagamento</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="manual-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Dinheiro", "Pix", "Cartão de Débito", "Cartão de Crédito"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Valor recebido</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 h-11 font-mono" data-testid="manual-amount" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManual(null)}>Cancelar</Button>
            <Button onClick={doManual} className="bg-neutral-900 hover:bg-neutral-800" data-testid="manual-confirm">Confirmar baixa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
